/**
 * @file GlobalFooter/component.js
 * @copyright 2015 CBC/Radio-Canada. All rights reserved.
 */

import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import classNames from "classnames";
import Image from "../ImageWrapper/component";
import { FOOTER, EXTERNAL_FOOTER_PATH } from "./constants";
import FOOTER_LOGO from "./resources/images/logo_cbc-radio-canada.svg";
import LinkWrap from "../LinkWrap/component";
import ConsentModalButton from "./components/ConsentModalButton/component";
import "./styles/themes/_default.scss";
import { isConsentModalButtonType } from "../../utils/cmp/library";

/**
 * Global footer.
 *
 * @param {Object} props The component props
 * @returns {React.Component} The GlobalFooter component
 */

export function GlobalFooter(props) {
    const fixed = useSelector((state) => state.fixed);
    const isExternalFooter =
        props.isExternalFooter === true ||
        (props.route && props.route.path.startsWith(EXTERNAL_FOOTER_PATH));

    const classes = classNames("globalFooter", "sclt-footer", {
        externalFooter: isExternalFooter,
    });

    const newProps = Object.assign(
        {
            extended: true,
            isExternal: isExternalFooter,
        },
        props
    );

    const year = (fixed && fixed.year) || "";

    const extendedFooter = newProps.extended ? (
        <ExtendedFooter {...newProps} />
    ) : null;

    if (fixed && fixed.isWebView) return null;

    return (
        <footer
            className={classes}
            aria-label="CBC Footer"
            data-testid="footer"
            data-cy="globalFooter"
        >
            {extendedFooter}
            <div className="basicFooter" data-testid="basic-footer">
                <ul className="footerBody footerList">
                    <li className="footerColumn extendedLogo">
                        <LinkWrap
                            className="extendedLogoLink"
                            url="https://cbc.radio-canada.ca/en"
                            external={true}
                            aria-label="CBC/Radio-Canada"
                        >
                            <Image url={FOOTER_LOGO} />
                        </LinkWrap>
                    </li>
                    <li className="footerColumn copyright">
                        <span role="img" aria-label="copyright">
                            &copy;
                        </span>
                        {year} CBC/Radio-Canada. All rights reserved.{" "}
                    </li>
                    <li className="footerColumn radioCanada">
                        <LinkWrap
                            className="radioCanada"
                            url="https://ici.radio-canada.ca/"
                            hrefLang="fr-CA"
                            lang="fr-CA"
                            external={true}
                        >
                            <span>Visitez Radio-Canada.ca</span>
                        </LinkWrap>
                    </li>
                </ul>
            </div>
        </footer>
    );
}

/**
 * Render a list of footer links/buttons for the given section.
 *
 * @param {object} itemsList The list of links/buttons to be rendered
 * @return {component} JSX markup for the list of links/buttons.
 */
function renderSection(itemsList, externalFooter) {
    return itemsList.map((item, index) => {
        const isExternal = externalFooter === true || item.type === "external";
        const isConsentModalButton = isConsentModalButtonType(item.type);
        const url = item.link;
        const optionalProps = {};

        if (isExternal) {
            optionalProps.target = "_blank";
        }

        if (isConsentModalButton) {
            return (
                <li className="footerItem" key={`${item.title}-${index}`}>
                    <ConsentModalButton item={item} isExternal={isExternal} />
                </li>
            );
        }

        return (
            <li className="footerItem" key={`${item.title}-${index}`}>
                <LinkWrap
                    className="footerLink"
                    key={index}
                    url={url}
                    external={isExternal}
                    {...optionalProps}
                >
                    {item.title}
                </LinkWrap>
            </li>
        );
    });
}

/**
 * Render the top part of the Footer displaying the Connect, Contact and
 * Accessibility info.
 *
 * @returns {React.Component} The ExtendedFooter component
 */
function ExtendedFooter(props) {
    // Get the list of Footer items from the config file
    const footerNavigationMap = FOOTER;

    return (
        <div className="footerBody">
            <h2 className="a11y">Footer Links</h2>

            <div className="footerColumn account">
                <h3 className="footerHeading">My Account</h3>
                <ul className="footerList">
                    {renderSection(
                        footerNavigationMap.myaccount,
                        props.isExternal
                    )}
                </ul>
            </div>

            <div
                className="footerColumn connect"
                data-testid="footer-column-connect"
            >
                <h3 className="footerHeading">Connect with CBC</h3>
                <ul className="footerList">
                    {renderSection(
                        footerNavigationMap.connect,
                        props.isExternal
                    )}
                </ul>
            </div>

            <div className="footerColumn contact">
                <h3 className="footerHeading">Contact CBC</h3>
                <ul className="footerList">
                    {renderSection(
                        footerNavigationMap.contact,
                        props.isExternal
                    )}
                </ul>
                <p>
                    Audience Relations, CBC <br />
                    P.O. Box 500 Station A <br />
                    Toronto, ON <br /> Canada, M5W 1E6{" "}
                </p>
                <p>
                    Toll-free (Canada only): <br /> 1-866-306-4636
                </p>
            </div>

            <div className="footerColumn about">
                <h3 className="footerHeading">About CBC</h3>
                <ul className="footerList">
                    {renderSection(footerNavigationMap.about, props.isExternal)}
                </ul>
            </div>

            <div className="footerColumn connect">
                <h3 className="footerHeading">Services</h3>
                <ul className="footerList">
                    {renderSection(
                        footerNavigationMap.services,
                        props.isExternal
                    )}
                </ul>
            </div>

            <div className="footerColumn accessibility">
                <h3 className="footerHeading">Accessibility</h3>
                <div className="footerColumnBody">
                    <p>
                        It is a priority for CBC to create products that are
                        accessible to all in Canada including people with
                        visual, hearing, motor and cognitive challenges.
                    </p>
                    <p>
                        Closed Captioning and Described Video is available for
                        many CBC shows offered on{" "}
                        <LinkWrap
                            className="footerLink cbcWatch"
                            url="https://gem.cbc.ca/"
                            external={true}
                        >
                            CBC Gem
                        </LinkWrap>
                        .
                    </p>
                    <ul
                        className="footerList"
                        data-testid="footer-column-accessibility-ul"
                    >
                        {renderSection(
                            footerNavigationMap.accessibility,
                            props.isExternal
                        )}
                    </ul>
                </div>
            </div>
        </div>
    );
}

export default GlobalFooter;

GlobalFooter.propTypes = {
    extended: PropTypes.bool,
    isExternalFooter: PropTypes.bool,
};
